// ./src/components/features/CreateEventWizard/steps/ScheduleStep.tsx
//
// "When does it happen?" — the wizard's Schedule step (ROADMAP A2b #62 P6, owner 2026-09-30: "we need to offer to edit
// event schedule … event can be one time, or across multiple days or be recurring"). Shown for a REPEATING event, and for
// any other when the host chose the Sessions feature (owner, 2026-10-01 — `scheduleNeedsStep`) or it still carries
// repeating dates from before a switch (the offer to remove them is here). Basics picked
//   · Repeats — the repeat RULE (the Schedule page's own `RecurrenceRuleEditor`, held in the draft); the dates are
//     created from it when the host continues or finishes, or at once with "Create the dates now", and listed below;
//   · Agenda  — the SESSIONS (sub-events over one or more days), added and edited in the Schedule page's own
//     `SlotDrawer`: real rows the moment they are saved.
//   · One time — OPTIONAL sessions the same way (owner, 2026-09-30: a one-day networking event had nowhere to enter its
//     schedule); on a one-time event they are opt-ins on top of the one reply, never a required pick.
//
// ⚠ Reads, in order: the event (not read yet → skeleton; failed or GONE → the error with a retry — never an endless
//   skeleton), then the dates / sessions through `WizardListSection` (the four states every list owes).
// ⚠ A repeating event with NO dates is a page with nothing to reply to — Next and Finish create them from the rule the
//   host accepted even untouched (`ensureRecurringDates`).
// ⚠ "Create the dates now" runs as the WIZARD's save (`runExclusive`) — Next pressed meanwhile made every date twice.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { OP_RESULT_CODES, OpResult } from '@sdflc/api-helpers';
import { STATUSES } from '@sdflc/utils';
import { useTranslation } from 'react-i18next';

import { useEventTimeSlots, useToast } from '@/hooks';
import {
  Event,
  EventArtist,
  EventArtistService,
  EventGuest,
  EventGuestService,
  EventService,
  EventTimeSlot,
  EventTimeSlotService,
  eventArtistDisplayName,
  getGuestDisplayName,
} from '@/utils/api';
import { AppIcon, ConfirmModal, ErrorAlert } from '@/components/generic';
import { RecurrenceRuleEditor } from '@/pages/private/EventSchedulePage/RecurrenceRuleEditor';
import { SlotDrawer } from '@/pages/private/EventSchedulePage/SlotDrawer';
import { defaultRecurrenceRule } from '@/pages/private/EventSchedulePage/recurrenceDefaults';
import { SESSION_PEOPLE_ROLES } from '@/pages/private/EventSchedulePage/slotSpeakerRules';

import { useEventWizard } from '../WizardContext';
import { SCHEDULE_MODE, recurrenceSeedKey } from '../wizardTypes';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { saveRecurrenceAndGenerate } from '../scheduleApply';
import { runBulkWrites, refusalReason } from '@/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

/** How many generated dates the step lists (the rest are on the Schedule page). */
const DATES_SHOWN = 8;

const ScheduleStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { draft, patchDraft, saving, runExclusive, goToStep } = useEventWizard();
  const { slots, isLoaded, error: slotsError, reload } = useEventTimeSlots(eventId);

  // The event, read here (`useEvent` cannot tell "not read yet" from "read, and gone").
  const [event, setEvent] = useState<Event | null>(null);
  const [eventError, setEventError] = useState<OpResult | null>(null);

  const loadEvent = useCallback(async () => {
    setEventError(null);

    const res = await EventService.get(eventId);
    const row = res.didSucceed() ? ((res.getDataFirst() as Event | undefined) ?? null) : null;

    if (!row?.id) {
      setEventError(res.didSucceed() ? new OpResult({ code: OP_RESULT_CODES.NOT_FOUND }).addError('', 'This event could not be found.') : res);

      return;
    }

    setEvent(row);
  }, [eventId]);

  useEffect(() => {
    void loadEvent();
  }, [loadEvent]);

  const [drawer, setDrawer] = useState<{ slot: EventTimeSlot | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<EventTimeSlot | null>(null);
  const [removing, setRemoving] = useState(false);
  const [generateError, setGenerateError] = useState<OpResult | null>(null);
  const [clearingGenerated, setClearingGenerated] = useState(false);
  const [confirmClearGenerated, setConfirmClearGenerated] = useState(false);

  const isRecurring = draft.scheduleMode === SCHEDULE_MODE.RECURRING;
  const tz = event?.timezone || draft.timezone || dayjs.tz.guess();
  const ordered = useMemo(() => [...slots].sort((a, b) => String(a.startsAt ?? '').localeCompare(String(b.startsAt ?? ''))), [slots]);
  // Dates made from a repeat pattern before the host switched this event to an agenda (review, 2026-09-30).
  const leftoverGenerated = useMemo(() => (isRecurring ? [] : slots.filter((s) => s.isGenerated && !s.isOverridden)), [isRecurring, slots]);

  // The rule the step edits: the event's saved one (the host's), else the default from its start and end. An UNTOUCHED
  // default is rebuilt when Basics' start moves (`recurrenceSeed`); the host's own rule is never overwritten.
  useEffect(() => {
    if (!isRecurring || !event?.startsAt) {
      return;
    }

    const seed = recurrenceSeedKey(event);

    if (draft.recurrence == null) {
      const saved = event.settings?.recurrence ?? null;

      patchDraft({
        recurrence: saved ?? defaultRecurrenceRule(event.startsAt, tz, event.endsAt),
        recurrenceEdited: false,
        recurrenceSeed: saved ? null : seed,
      });

      return;
    }

    if (draft.recurrenceSeed != null && draft.recurrenceSeed !== seed) {
      patchDraft({ recurrence: defaultRecurrenceRule(event.startsAt, tz, event.endsAt), recurrenceEdited: true, recurrenceSeed: seed });
    }
  }, [isRecurring, event, draft.recurrence, draft.recurrenceSeed, tz, patchDraft]);

  // #70 — who is on each session: its acts, then its people, by name. Read once for the step; a failed read only costs
  // the names on the rows (they are a reminder, not the editor — the drawer has the real pickers).
  const [actNames, setActNames] = useState<Map<string, string>>(new Map());
  const [peopleNames, setPeopleNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let alive = true;

    Promise.all([
      EventArtistService.listAll({ eventId: [eventId], status: [STATUSES.ACTIVE] }),
      EventGuestService.listAll({ eventId: [eventId], guestRole: [...SESSION_PEOPLE_ROLES], status: [STATUSES.ACTIVE] }),
    ]).then(([acts, people]) => {
      if (!alive) {
        return;
      }

      const named = <T extends { id?: string | null }>(rows: unknown, name: (row: T) => string) =>
        new Map((Array.isArray(rows) ? (rows as T[]) : []).filter((r) => !!r.id).map((r) => [r.id as string, name(r)]));

      setActNames(acts.didSucceed() ? named<EventArtist>(acts.data, eventArtistDisplayName) : new Map());
      setPeopleNames(people.didSucceed() ? named<EventGuest>(people.data, getGuestDisplayName) : new Map());
    });

    return () => {
      alive = false;
    };
  }, [eventId]);

  const whoOf = (slot: EventTimeSlot): string =>
    [...(slot.eventArtistIds ?? []).map((id) => (id ? actNames.get(id) : undefined)), ...(slot.speakerGuestIds ?? []).map((id) => (id ? peopleNames.get(id) : undefined))]
      .filter((name): name is string => !!name)
      .join(', ');

  /** The row's second line: when (if the name is the title), and who is on it. */
  const subtitleOf = (slot: EventTimeSlot): string | null => {
    const who = whoOf(slot);
    const parts = [slot.name?.trim() ? whenOf(slot) : '', who ? t('events:wizard.schedule.with', { defaultValue: 'With {{names}}', names: who }) : ''];

    return parts.filter(Boolean).join(' · ') || null;
  };

  const whenOf = (slot: EventTimeSlot): string => {
    if (!slot.startsAt) {
      return '';
    }

    const start = dayjs.utc(slot.startsAt).tz(tz);
    const end = slot.endsAt ? dayjs.utc(slot.endsAt).tz(tz) : null;
    const endPart = end ? (end.isSame(start, 'day') ? ` – ${end.format('h:mm A')}` : ` – ${end.format('ddd, MMM D, h:mm A')}`) : '';

    return `${start.format('ddd, MMM D, YYYY · h:mm A')}${endPart}`;
  };

  const generateNow = async () => {
    const rule = draft.recurrence;

    if (!rule) {
      return;
    }

    setGenerateError(null);

    const res = await runExclusive(() => saveRecurrenceAndGenerate(eventId, rule));

    if (!res.didSucceed()) {
      setGenerateError(res);

      return;
    }

    patchDraft({ recurrenceEdited: false });
    void reload();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await EventTimeSlotService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void reload();
  };

  const clearGenerated = async () => {
    setClearingGenerated(true);

    const results = await runBulkWrites(leftoverGenerated, (s) => EventTimeSlotService.remove(s.id as string));

    setClearingGenerated(false);
    setConfirmClearGenerated(false);

    const failed = results.filter((r) => !r.didSucceed()).length;

    if (failed > 0) {
      toast.error(t('events:wizard.schedule.clearFailed', { defaultValue: '{{count}} date(s) could not be removed — try again on the Schedule page.', count: failed }));
    }

    void reload();
  };

  if (eventError) {
    return (
      <Stack gap="xs">
        <ErrorAlert error={eventError} title={t('events:wizard.schedule.loadFailed', { defaultValue: 'Could not read the event' })} page="Create event" operation="eventGet" />
        <Group>
          <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => void loadEvent()}>
            {t('common:retry', { defaultValue: 'Try again' })}
          </Button>
        </Group>
      </Stack>
    );
  }

  if (!event) {
    return <Skeleton height={260} radius="md" />;
  }

  // ⚠ Dates and sessions hang off the START: without one there is nothing to repeat or to place a session in.
  if (!event.startsAt) {
    return (
      <Alert variant="light" color="yellow" icon={<AppIcon iconName="CalendarEvent" size={18} />}>
        <Stack gap="xs" align="flex-start">
          <Text size="md">
            {t('events:wizard.schedule.needsStart', { defaultValue: 'Set when your event starts first — the dates and sessions are placed from it.' })}
          </Text>
          <Button variant="light" size="md" disabled={saving} onClick={() => goToStep('basics')}>
            {t('events:wizard.schedule.toBasics', { defaultValue: 'Go to Basics' })}
          </Button>
        </Stack>
      </Alert>
    );
  }

  const shown = isRecurring ? ordered.slice(0, DATES_SHOWN) : ordered;
  const more = isRecurring ? Math.max(0, ordered.length - DATES_SHOWN) : 0;
  const rule = draft.recurrence;
  const startClock = dayjs.utc(event.startsAt).tz(tz).format('HH:mm');
  // The host's OWN rule no longer starts when the event does (Basics changed since) — said, never silently rewritten.
  const ruleOffStart = isRecurring && !!rule && draft.recurrenceSeed == null && !!rule.startTime && rule.startTime !== startClock;

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {isRecurring
          ? t('events:wizard.schedule.introRecurring', {
              defaultValue: 'Set how your event repeats. The dates are created from this pattern when you continue — guests reply to, or buy for, one date.',
            })
          : draft.scheduleMode === SCHEDULE_MODE.AGENDA
            ? t('events:wizard.schedule.introAgenda', {
                defaultValue: 'Add the sessions of your event — a ceremony and a reception, the talks of each day. Guests see them in order on your event page.',
              })
            : t('events:wizard.schedule.introSingle', {
                defaultValue:
                  'Optional: break your event into parts — doors open, talks, dinner. Guests see them in order on your event page. Skip this for one simple gathering.',
              })}
      </Text>

      {isRecurring && rule ? (
        <Card withBorder radius="md" padding="lg">
          <Stack gap="md">
            <Text fw={600} size="md">
              {t('events:schedulePage.patternTitle', { defaultValue: 'Repeat pattern' })}
            </Text>
            {ruleOffStart ? (
              <Text size="md" c="orange.8">
                {t('events:wizard.schedule.ruleOffStart', {
                  defaultValue: 'Your event now starts at {{time}} on Basics, but this pattern starts at {{ruleTime}} — check it before you continue.',
                  time: dayjs.utc(event.startsAt).tz(tz).format('h:mm A'),
                  ruleTime: rule.startTime,
                })}
              </Text>
            ) : null}
            <RecurrenceRuleEditor value={rule} onChange={(next) => patchDraft({ recurrence: next, recurrenceEdited: true, recurrenceSeed: null })} timeZone={draft.timezone || null} />
            {generateError ? (
              <ErrorAlert error={generateError} title={t('events:schedulePage.generateFailed', { defaultValue: 'Could not generate the schedule.' })} page="Create event" operation="eventTimeSlotGenerate" />
            ) : null}
            <Group justify="flex-end">
              <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} loading={saving} onClick={() => void generateNow()}>
                {t('events:wizard.schedule.generateNow', { defaultValue: 'Create the dates now' })}
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : null}

      {leftoverGenerated.length > 0 ? (
        <Alert variant="light" color="yellow" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
          <Stack gap="xs" align="flex-start">
            <Text size="md">
              {t('events:wizard.schedule.leftover', {
                defaultValue: '{{count}} date(s) were created from a repeat pattern before this event stopped repeating. They are listed as sessions below.',
                count: leftoverGenerated.length,
              })}
            </Text>
            <Button variant="light" color="red" size="md" disabled={saving} loading={clearingGenerated} onClick={() => setConfirmClearGenerated(true)}>
              {t('events:wizard.schedule.clearLeftover', { defaultValue: 'Remove the repeating dates' })}
            </Button>
          </Stack>
        </Alert>
      ) : null}

      <WizardListSection
        title={isRecurring ? t('events:wizard.schedule.dates', { defaultValue: 'Dates' }) : t('events:wizard.schedule.sessions', { defaultValue: 'Sessions' })}
        addLabel={isRecurring ? t('events:wizard.schedule.addDate', { defaultValue: 'Add a date' }) : t('events:wizard.schedule.addSession', { defaultValue: 'Add a session' })}
        onAdd={() => setDrawer({ slot: null })}
        // ⓘ Not plan-gated: sessions and dates are the event itself.
        gate={null}
        gateWhat=""
        disabled={saving}
        isLoaded={isLoaded}
        readError={slotsError}
        onRetry={() => void reload()}
        errorTitle={t('events:wizard.schedule.slotsFailed', { defaultValue: 'Could not load the schedule' })}
        operation="eventTimeSlotList"
        emptyText={
          isRecurring
            ? t('events:wizard.schedule.noDates', { defaultValue: 'No dates yet — they are created from the pattern when you continue.' })
            : t('events:wizard.schedule.noSessions', { defaultValue: 'No sessions yet.' })
        }
        isEmpty={ordered.length === 0}
      >
        {shown.map((slot) => (
          <WizardItemRow
            key={slot.id}
            icon={isRecurring ? 'CalendarEvent' : 'CalendarTime'}
            title={slot.name?.trim() || whenOf(slot)}
            subtitle={subtitleOf(slot)}
            disabled={saving}
            onEdit={() => setDrawer({ slot })}
            onRemove={() => setPendingRemove(slot)}
          />
        ))}
        {more > 0 ? (
          <Text size="md" c="dimmed">
            {t('events:wizard.schedule.more', { defaultValue: '…and {{count}} more — all of them are on the Schedule page.', count: more })}
          </Text>
        ) : null}
      </WizardListSection>

      <SlotDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        event={event}
        slot={drawer?.slot ?? null}
        showAgenda={!isRecurring}
        siblings={slots}
        onSaved={() => {
          setDrawer(null);
          void reload();
        }}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={
          isRecurring
            ? t('events:wizard.schedule.removeDateTitle', { defaultValue: 'Remove this date?' })
            : t('events:wizard.schedule.removeSessionTitle', { defaultValue: 'Remove this session?' })
        }
        message={
          isRecurring
            ? t('events:wizard.schedule.removeDateMessage', { defaultValue: 'It is removed from the event. Creating the dates from the pattern again may bring it back.' })
            : t('events:wizard.schedule.removeSessionMessage', {
                defaultValue: '“{{name}}” will be removed from this event.',
                name: pendingRemove ? pendingRemove.name?.trim() || whenOf(pendingRemove) : '',
              })
        }
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />

      <ConfirmModal
        opened={confirmClearGenerated}
        onClose={() => setConfirmClearGenerated(false)}
        onConfirm={() => void clearGenerated()}
        type="danger"
        confirmInProgress={clearingGenerated}
        title={t('events:wizard.schedule.clearLeftoverTitle', { defaultValue: 'Remove {{count}} repeating date(s)?', count: leftoverGenerated.length })}
        message={t('events:wizard.schedule.clearLeftoverMessage', {
          defaultValue: 'The dates created from the old repeat pattern are removed. Sessions you added yourself stay.',
        })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const ScheduleStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return <ScheduleStepBody eventId={createdEventId} />;
};
